import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiTrash2, FiPlus, FiMinus, FiShoppingBag, FiArrowLeft } from 'react-icons/fi';

const CartContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 0;
`;

const CartContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const CartTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const CartItemsCount = styled.span`
  background: #2563eb;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const CartItems = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const CartItem = styled(motion.div)`
  display: flex;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.div`
  width: 80px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
`;

const ItemAuthor = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
`;

const ItemPrice = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  color: #374151;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  text-align: center;
  border: none;
  background: transparent;
  font-weight: 600;
  
  &:focus {
    outline: none;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: #fef2f2;
  color: #dc2626;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fee2e2;
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.125rem;
  }
`;

const SummaryLabel = styled.span`
  color: #6b7280;
`;

const SummaryValue = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  margin-top: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ContinueShoppingButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 16px;
  background: white;
  color: #2563eb;
  text-decoration: none;
  border: 2px solid #2563eb;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  margin-top: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EmptyCartIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #6b7280;
`;

const EmptyCartTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
`;

const EmptyCartText = styled.p`
  color: #6b7280;
  margin-bottom: 24px;
`;

const ShoppingCart = ({ cart, onRemoveFromCart, onUpdateQuantity }) => {
  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  if (cart.length === 0) {
    return (
      <CartContainer>
        <CartContent>
          <CartHeader>
            <BackButton to="/books">
              <FiArrowLeft />
              Back to Books
            </BackButton>
            <CartTitle>Your Cart</CartTitle>
          </CartHeader>

          <EmptyCart>
            <EmptyCartIcon>
              <FiShoppingBag size={32} />
            </EmptyCartIcon>
            <EmptyCartTitle>Your cart is empty</EmptyCartTitle>
            <EmptyCartText>Start adding books to your cart to see them here</EmptyCartText>
            <ContinueShoppingButton to="/books">
              Continue Shopping
            </ContinueShoppingButton>
          </EmptyCart>
        </CartContent>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <CartContent>
        <CartHeader>
          <BackButton to="/books">
            <FiArrowLeft />
            Back to Books
          </BackButton>
          <CartTitle>Your Cart</CartTitle>
          <CartItemsCount>{cart.length} item{cart.length !== 1 ? 's' : ''}</CartItemsCount>
        </CartHeader>

        <CartGrid>
          <CartItems>
            {cart.map((item, index) => (
              <CartItem
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <ItemImage>
                  <img src={item.image} alt={item.title} />
                </ItemImage>

                <ItemInfo>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemAuthor>by {item.author}</ItemAuthor>
                  <ItemPrice>${item.price}</ItemPrice>
                </ItemInfo>

                <ItemActions>
                  <QuantityControls>
                    <QuantityButton
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <FiMinus size={16} />
                    </QuantityButton>
                    <QuantityInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => onUpdateQuantity(item.id, parseInt(e.target.value) || 1)}
                      min="1"
                    />
                    <QuantityButton
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    >
                      <FiPlus size={16} />
                    </QuantityButton>
                  </QuantityControls>

                  <RemoveButton onClick={() => onRemoveFromCart(item.id)}>
                    <FiTrash2 size={14} />
                    Remove
                  </RemoveButton>
                </ItemActions>
              </CartItem>
            ))}
          </CartItems>

          <CartSummary>
            <SummaryTitle>Order Summary</SummaryTitle>
            
            <SummaryRow>
              <SummaryLabel>Subtotal ({cart.length} item{cart.length !== 1 ? 's' : ''})</SummaryLabel>
              <SummaryValue>${calculateSubtotal().toFixed(2)}</SummaryValue>
            </SummaryRow>
            
            <SummaryRow>
              <SummaryLabel>Tax</SummaryLabel>
              <SummaryValue>${calculateTax().toFixed(2)}</SummaryValue>
            </SummaryRow>
            
            <SummaryRow>
              <SummaryLabel>Total</SummaryLabel>
              <SummaryValue>${calculateTotal().toFixed(2)}</SummaryValue>
            </SummaryRow>

            <CheckoutButton to="/checkout">
              Proceed to Checkout
            </CheckoutButton>

            <ContinueShoppingButton to="/books">
              Continue Shopping
            </ContinueShoppingButton>
          </CartSummary>
        </CartGrid>
      </CartContent>
    </CartContainer>
  );
};

export default ShoppingCart;
